import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Browser } from '@capacitor/browser';

/** Numeric semver-ish compare: is `store` strictly newer than `installed`? */
function isNewerVersion(store: string, installed: string): boolean {
  const a = String(store).split('.').map((n) => parseInt(n, 10) || 0);
  const b = String(installed).split('.').map((n) => parseInt(n, 10) || 0);
  const len = Math.max(a.length, b.length);
  for (let i = 0; i < len; i++) {
    const x = a[i] || 0;
    const y = b[i] || 0;
    if (x > y) return true;
    if (x < y) return false;
  }
  return false;
}

interface AppStoreUpdateState {
  /** True only on native iOS when the App Store has a newer version and not dismissed. */
  updateAvailable: boolean;
  latestVersion: string | null;
  installedVersion: string | null;
  openStore: () => Promise<void>;
  dismiss: () => void;
}

/**
 * Detects when a newer NATIVE build is live on the App Store and surfaces a flag
 * for the update banner. Fully automatic — compares the running app version
 * (Capacitor App.getInfo) against Apple's public iTunes Lookup API, which always
 * reflects the version currently published on the App Store. No manual config to
 * maintain per release.
 *
 * iOS-native only (web/PWA uses the existing webview auto-update service).
 */
export function useAppStoreUpdate(): AppStoreUpdateState {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [latestVersion, setLatestVersion] = useState<string | null>(null);
  const [installedVersion, setInstalledVersion] = useState<string | null>(null);
  const [storeUrl, setStoreUrl] = useState<string | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const check = useCallback(async () => {
    try {
      if (!Capacitor.isNativePlatform() || Capacitor.getPlatform() !== 'ios') return;

      const info = await App.getInfo();
      const installed = info.version;
      const bundleId = info.id;
      if (!installed || !bundleId) return;
      setInstalledVersion(installed);

      // Apple's public lookup endpoint returns the live App Store version.
      const res = await fetch(
        `https://itunes.apple.com/lookup?bundleId=${encodeURIComponent(bundleId)}&_=${Date.now()}`,
        { cache: 'no-store' },
      );
      if (!res.ok) return;
      const data = await res.json();
      const result = data?.results?.[0];
      const storeVersion = result?.version ? String(result.version) : null;
      if (!storeVersion) return;

      if (isNewerVersion(storeVersion, installed)) {
        setLatestVersion(storeVersion);
        setStoreUrl(result.trackViewUrl || null);
        setUpdateAvailable(true);
        // Re-show on every check (launch / resume) until the user actually updates.
        setDismissed(false);
      } else {
        setUpdateAvailable(false);
      }
    } catch {
      // Non-fatal — never block the app over an update check.
    }
  }, []);

  useEffect(() => {
    check();
    let remove: (() => void) | undefined;
    (async () => {
      try {
        const handle = await App.addListener('resume', () => { check(); });
        remove = () => handle.remove();
      } catch {
        /* listener unavailable (web) */
      }
    })();
    return () => { if (remove) remove(); };
  }, [check]);

  const openStore = useCallback(async () => {
    if (!storeUrl) return;
    try {
      await Browser.open({ url: storeUrl });
    } catch {
      window.open(storeUrl, '_blank');
    }
  }, [storeUrl]);

  const dismiss = useCallback(() => setDismissed(true), []);

  return {
    updateAvailable: updateAvailable && !dismissed,
    latestVersion,
    installedVersion,
    openStore,
    dismiss,
  };
}
